import React, {useMemo, useState, useEffect} from 'react';
import {Layout, Separator, Collaboration, Icon, Card, Diary, Activities, Goals, Tabs, Flex, Notes} from 'components';
import {config, overEvery, noop, partial, useList, returnNull, useNamedSelector} from 'helpers';
import {SidebarPreferences as GoalsSidebarPreferences} from 'components/goals/sidebar-preferences';
import {SidebarPreferences as ActivitiesSidebarPreferences} from 'components/activities/sidebar-preferences';
import {SidebarPreferences as NotesSidebarPreferences} from 'components/notes/sidebar-preferences';
import {SidebarPreferences as DiarySidebarPreferences} from 'components/diary/sidebar-preferences';

export function SharePanelTabs({ Tab = GoalsTab, setTab }) {
  useEffect(() => {
    setTab(() => Tab);
  }, []);
  return (
    <Tabs className="spacing">
      <Tabs.Tab pressed={Tab == GoalsTab} onClick={() => setTab(() => GoalsTab)}>Goals</Tabs.Tab>
      <Tabs.Tab pressed={Tab == ActivitiesTab} onClick={() => setTab(() => ActivitiesTab)}>Activities</Tabs.Tab>
      <Tabs.Tab pressed={Tab == RoutinesTab} onClick={() => setTab(() => RoutinesTab)}>Routines</Tabs.Tab>
      { config.sw &&
        <Tabs.Tab pressed={Tab == HabitsTab} onClick={() => setTab(() => HabitsTab)}>Habits</Tabs.Tab>
      }
      <Tabs.Tab pressed={Tab == DiaryTab} onClick={() => setTab(() => DiaryTab)}>Journal</Tabs.Tab>
      <Tabs.Tab pressed={Tab == NotesTab} onClick={() => setTab(() => NotesTab)}>Notes</Tabs.Tab>
      <Flex className="pull-right secondary">
        <Tab.Preferences />
      </Flex>
    </Tabs>
  );
}

export function SharePanelModal({ modal, user, DefaultTab = GoalsTab }) {
  modal.setClassName('large');
  const [ Tab, setTab ] = useState(() => DefaultTab);
  return (
    <>
      <Layout.Header>
        <Flex className="justified">
          <p>Select what you want to share with <b>{ user.name || user.email }</b></p>
          <Icon className="secondary" name="close" onClick={modal.close} />
        </Flex>
        <Tabs className="spacing">
          <Tabs.Tab pressed={Tab == GoalsTab} onClick={() => setTab(() => GoalsTab) || modal.bounce()}>Goals</Tabs.Tab>
          <Tabs.Tab pressed={Tab == ActivitiesTab} onClick={() => setTab(() => ActivitiesTab) || modal.bounce()}>Activities</Tabs.Tab>
          <Tabs.Tab pressed={Tab == RoutinesTab} onClick={() => setTab(() => RoutinesTab) || modal.bounce()}>Routines</Tabs.Tab>
          <Tabs.Tab pressed={Tab == HabitsTab} onClick={() => setTab(() => HabitsTab) || modal.bounce()}>Habits</Tabs.Tab>
          <Tabs.Tab pressed={Tab == DiaryTab} onClick={() => setTab(() => DiaryTab) || modal.bounce()}>Journal</Tabs.Tab>
          <Tabs.Tab pressed={Tab == NotesTab} onClick={() => setTab(() => NotesTab) || modal.bounce()}>Notes</Tabs.Tab>
          <Flex className="pull-right secondary">
            <Tab.Preferences />
          </Flex>
        </Tabs>
      </Layout.Header>
      <Tab user={user} />
    </>
  );
}

function GoalsTab({ user, filter = Boolean }) {
  let heading = '';
  const [ list, Last ] = useList(useNamedSelector('goals.list', {
    module: 'goals',
    filter: ({ id }) => filter({id, context: 'goals'})
  }));
  const Menu = useMemo(() => partial(ContextMenu, { context: 'goals', user }), [user]);
  return (
    <div>
      { !list.length && (filter == Boolean ? 'You have no goals.' : 'You have not shared any goals.') }
      { list.map(goal => goal.heading !== undefined
        ? <Separator key={heading = goal.heading} heading={goal.heading} />
        : <Goals.Single.Card
            hasThumbnail
            id={goal.id}
            key={heading + goal.id}
            ActionMenu={Menu}
            Status={returnNull}
          />
      ) }
      <Last />
    </div>
  );
}
GoalsTab.Preferences = partial(GoalsSidebarPreferences);

function ActivitiesTab({ user, context = 'activities', filter = Boolean }) {
  let heading = '';
  const [ list, Last ] = useList(useNamedSelector('activities.list', {
    module: context,
    filter: overEvery(
      ({ googleEventId, iOSEventId }) => !googleEventId && !iOSEventId,
      ({ id }) => filter({id, context: 'activities'})
    )
  }));
  const Menu = useMemo(() => partial(ContextMenu, { context: 'activities', user }), [user]);
  return (
    <div>
      { !list.length && (filter == Boolean ? `You have no ${context}.` : `You have not shared any ${context}.`) }
      { list.map(activity => activity.heading !== undefined
        ? <Separator key={heading=activity.heading} heading={activity.heading} icon={activity.heading} />
        : <Activities.Card
            onClick={noop}
            className="has-right-menu"
            detailed={false}
            id={activity.id}
            key={heading + activity.id}
            ActionMenu={Menu}
            Status={returnNull}
          />
      ) }
      <Last />
    </div>
  );
}
ActivitiesTab.Preferences = partial(ActivitiesSidebarPreferences, { isSidebar: true, module: 'activities' });

function RoutinesTab(props) {
  return (
    <ActivitiesTab context="routines" {...props} />
  );
}
RoutinesTab.Preferences = partial(ActivitiesSidebarPreferences, { isSidebar: true, module: 'routines' });

function HabitsTab(props) {
  return (
    <ActivitiesTab context="habits" {...props} />
  );
}
HabitsTab.Preferences = partial(ActivitiesSidebarPreferences, { isSidebar: true, module: 'habits' });

function NotesTab({ user, filter = Boolean }) {
  let heading = '';
  const [ list, Last ] = useList(useNamedSelector('notes.list', {
    filter: ({ id }) => filter({id, context: 'notes'})
  }));
  const Menu = useMemo(() => partial(ContextMenu, { context: 'notes', user }), [user]);
  return (
    <div>
      { !list.length && (filter == Boolean ? 'You have no notes.' : 'You have not shared any notes.') }
      { list.map(note => note.heading !== undefined
        ? <Separator key={heading = note.heading} heading={note.heading} />
        : <Notes.Card key={heading + note.id} {...note} ActionMenu={Menu} className="has-right-menu" Status={returnNull} navLink={false} />
      ) }
      <Last />
    </div>
  );
}

NotesTab.Preferences = NotesSidebarPreferences;

function DiaryTab({ user, filter = Boolean }) {
  const { sort } = useNamedSelector('diary.options');
  let groups = useNamedSelector('diary.list');
  groups = sort == 'newest' ? groups.slice().reverse() : groups;
  const [ list, Last ] = useList(groups);
  const Menu = useMemo(() => partial(ContextMenu, { context: 'diary', user }), [user]);
  return (
    <div>
      { !list.length && (filter == Boolean ? 'You have no journals.' : 'You have not shared any journals.') }
      { list.map(({ date }) =>
        <Diary.Group
          className="has-right-menu"
          date={date}
          filter={({ id }) => filter({id, context: 'diary'})}
          key={date}
          ActionMenu={Menu}
          onClick={noop}
          Status={returnNull}
        />
      ) }
      <Last />
    </div>
  );
}

DiaryTab.Preferences = DiarySidebarPreferences;

function ContextMenu({ context, user }, { id }) {
  return (
    <Card.RightMenu>
      <Collaboration.ShareButton id={id} context={context} collaborator={user} />
    </Card.RightMenu>
  );
}
